import { useMemo, useRef } from 'react';

import { fg } from '@confluence/feature-gating';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';

import type { PreloadedSearchPayload } from './preloadSearch.ts';

export const usePreloadDataProvider = () => {
	const isTransition = useIsTransition();
	// Immediate runtime guard for non-initial-load mode.
	const isTransitionRef = useRef(isTransition);
	isTransitionRef.current = isTransition;
	// Sticky provider-local retirement flag for explicitly consumed preload data.
	const clearedRef = useRef(false);

	return useMemo(
		() => ({
			getPreloadedSearch: ({
				cloudId: requestedCloudId,
			}: {
				cloudId: string;
			}): PreloadedSearchPayload | null => {
				if (!fg('search_config_ssr_fg')) {
					return null;
				}

				if (isTransitionRef.current) {
					return null;
				}

				const preloadedSearch = window.__PRELOADED_SEARCH__;

				if (
					clearedRef.current ||
					!preloadedSearch ||
					preloadedSearch.cloudId !== requestedCloudId
				) {
					return null;
				}

				return preloadedSearch as PreloadedSearchPayload;
			},
			clearPreloadedSearch: ({ cloudId: requestedCloudId }: { cloudId: string }) => {
				const preloadedSearch = window.__PRELOADED_SEARCH__;

				if (preloadedSearch?.cloudId === requestedCloudId) {
					clearedRef.current = true;
				}
			},
		}),
		[],
	);
};
